import { useState, useEffect, useRef } from "react";
import { studyPreProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { Progress } from "@/components/ui/progress";
import { StudiesLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { ReviewMediaSkeleton } from "@/features/studies/components/review-media-skeleton";
import { useSegment } from "@/hooks/use-segment";
import { useGetStudy } from "@/api/studio/studies/queries";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useQueryClient } from "@tanstack/react-query";

/* Constants */
const POLL_INTERVAL = 3000;

/* Prop Types */
interface StudySceneUploadRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudySceneUploadRoute component
 *
 * Triggers study-level pre-processing and polls until every scene reaches a terminal state.
 */
export const StudySceneUploadRoute = ({ projectId, studyId }: StudySceneUploadRouteProps) => {
  /* Queries */
  const { data: study } = useGetStudy(projectId, studyId);

  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [isActive, setIsActive] = useState(false);
  const hasStarted = useRef(false);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-upload");
  const qc = useQueryClient();

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Side effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startPreprocessing = async () => {
      setIsActive(true);

      await studyPreProcess(projectId, studyId);
      let finalStatuses: StudyAssetStatus[] = [];

      // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
      while (true) {
        const statuses = await getStudyStatus(projectId, studyId, "preprocess");
        finalStatuses = statuses;
        setAssetStatuses(statuses);

        const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
        if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }

      segmentTrackEvent("Study Processed", {
        projectId,
        studyId,
        studyType: "scene",
        processType: "preprocess",
        totalSucceeded: finalStatuses.filter((status) => status.status === "SUCCEEDED").length,
        totalFailed: finalStatuses.filter((status) => status.status === "FAILED").length,
        useCaseName: study?.useCase.name,
        useCaseId: study?.useCase.id,
      });

      // Invalidate the data cache for the study so that the next step has the latest data
      qc.invalidateQueries({ queryKey: ["pre-processed-data", projectId, studyId] });

      navigateToNext();
    };

    void startPreprocessing();
  }, []);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {isActive && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video src="/videos/review-animation.webm" className="w-48 pb-2" autoPlay muted loop />
            <h2>
              Reviewing {succeededCount} of {totalCount || "..."} {(totalCount || 0) === 1 ? "scene" : "scenes"}
            </h2>
            {totalCount > 0 && <Progress value={(succeededCount / totalCount) * 100} staticColorClass="bg-success" className="w-1/2" />}
          </div>
        </FullPageLoader>
      )}
      <ReviewMediaSkeleton uploadLength={totalCount} />
    </StudiesLayout>
  );
};
